import L from 'leaflet'
import {
  CityMarker,
  CLUSTER_CITIES_MAX_CHARS,
  CLUSTER_TIERS,
  MOTIF_MARKER_LABEL_CLASS,
  MOTIF_MARKER_SIZE,
  MotifMarkerData,
} from './mapConfig'

const clusterTierFor = (count: number) =>
  CLUSTER_TIERS.find(tier => count <= tier.maxCount) ?? CLUSTER_TIERS[CLUSTER_TIERS.length - 1]

/** Distinct settlement names of a cluster's children, alphabetically sorted. */
const clusterCityNames = (cluster: L.MarkerCluster): string[] => {
  const names = (cluster.getAllChildMarkers() as CityMarker[])
    .map(marker => marker.cityName)
    .filter((name): name is string => !!name)
    .sort((a, b) => a.localeCompare(b))

  return Array.from(new Set(names))
}

export const createMotifIcon = (data: MotifMarkerData, cityName?: string) => {
  const size = MOTIF_MARKER_SIZE
  const imageUrl = data.motif?.vectorizedUrl || data.motif?.originalSVGUrl || data.motif?.photoUrl
  const content = imageUrl ? `<img src="${imageUrl}" alt="" />` : String(data.coordinate.id)
  // the settlement label is baked into the icon rather than bound as a Leaflet
  // tooltip: it then exists exactly when the pin does — no separate lifecycle to
  // sync as markers fold in and out of clusters
  const label = cityName ? `<div class="${MOTIF_MARKER_LABEL_CLASS}">${cityName}</div>` : ''

  return L.divIcon({
    className: 'motif-marker',
    html: `${label}<div class="motif-marker-image">${content}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

/**
 * The cluster's city label: whole settlement names only, never a cut one. Names
 * are listed while they fit CLUSTER_CITIES_MAX_CHARS (the first always is), the
 * rest fold into a "+N" counter. Each name is its own span so the label wraps
 * between names rather than inside one.
 */
const clusterCitiesLabel = (cities: string[]) => {
  if (!cities.length) return ''

  const shown: string[] = []
  let length = 0
  for (const name of cities) {
    // 2 == the ", " separator
    length += name.length + (shown.length ? 2 : 0)
    if (shown.length && length > CLUSTER_CITIES_MAX_CHARS) break
    shown.push(name)
  }
  const hiddenCount = cities.length - shown.length
  const names = shown.map(
    (name, index) =>
      `<span class="marker-cluster-city">${name}${index < shown.length - 1 ? ',' : ''}</span>`,
  )
  const counter = hiddenCount
    ? [`<span class="marker-cluster-cities-more">+${hiddenCount}</span>`]
    : []

  return `<div class="marker-cluster-cities">${[...names, ...counter].join(' ')}</div>`
}

export const createClusterIcon = (cluster: L.MarkerCluster, withCities: boolean) => {
  const count = cluster.getChildCount()
  const { category, size } = clusterTierFor(count)
  const citiesLabel = clusterCitiesLabel(withCities ? clusterCityNames(cluster) : [])

  return L.divIcon({
    html: `<span>${count}</span>${citiesLabel}`,
    className: `marker-cluster marker-cluster--${category}`,
    iconSize: L.point(size, size),
  })
}

/**
 * leaflet.markercluster unspiderfies a cluster on *any* map click via a private
 * `_unspiderfyWrapper`. We keep that default behaviour but let the caller veto a
 * single call (used to protect clicks on our own motif markers).
 */
export const guardClusterAutoUnspiderfy = (
  clusterGroup: L.MarkerClusterGroup,
  shouldSuppress: () => boolean,
) => {
  const internal = clusterGroup as unknown as { _unspiderfyWrapper: () => void }
  const original = internal._unspiderfyWrapper.bind(clusterGroup)
  internal._unspiderfyWrapper = () => {
    if (!shouldSuppress()) original()
  }
}

/** Toggles the CSS classes that hide non-spiderfied markers while a cluster is open. */
export const setSpiderfyVisible = (
  map: L.Map,
  event: L.MarkerClusterSpiderfyEvent,
  visible: boolean,
) => {
  const method = visible ? 'add' : 'remove'
  map.getContainer().classList[method]('spiderfying')
  event.cluster.getElement()?.classList[method]('spiderfy-visible')
  event.markers.forEach(marker => marker.getElement()?.classList[method]('spiderfy-visible'))
}
